<template>
  <BaseTemplate app-name="Gestão de Acessos" page-title="">
    <template #content>
      <div class="w-full py-6 px-4 md:px-8">
        <button
          @click="voltar"
          class="hidden md:inline-flex mb-5 items-center gap-2 bg-blue-500 dark:bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors dark:hover:bg-blue-700"
        >
          <SquareArrowLeft class="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <div class="flex justify-between items-center mb-6">
          <h1 class="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <LockIcon class="w-6 h-6 text-blue-500" />
            Gestão de Acessos
          </h1>
        </div>

        <div class="flex flex-wrap gap-3 mb-6">
          <button
            @click="activeTab = 'usuarios'"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeTab === 'usuarios'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
            ]"
          >
            Usuários
          </button>
          <button
            @click="activeTab = 'permissoes'"
            :class="[
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeTab === 'permissoes'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
            ]"
          >
            Permissões por grupo
          </button>
        </div>

        <div v-if="activeTab === 'usuarios'" class="flex flex-col sm:flex-row gap-4 mb-6">
          <input
            v-model="filter"
            type="text"
            placeholder="Buscar por nome, usuário ou email..."
            class="w-full sm:w-1/2 border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div v-else class="flex flex-col sm:flex-row gap-4 mb-6">
          <input
            v-model="groupFilter"
            type="text"
            placeholder="Buscar grupo..."
            class="w-full sm:w-1/2 border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <p v-if="error" class="text-red-500 text-center mb-4">{{ error }}</p>

        <div v-if="activeTab === 'usuarios'" class="hidden sm:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto dark:text-white text-gray-800">
          <table class="w-full">
            <thead class="bg-blue-500 dark:bg-blue-600 text-white dark:text-gray-300 text-sm uppercase tracking-wide">
              <tr>
                <th @click="ordenarPor('nome')" class="cursor-pointer px-4 py-3 text-left">
                  Nome <span v-if="colunaOrdenada === 'nome'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th @click="ordenarPor('usuario')" class="cursor-pointer px-4 py-3 text-left">
                  Usuário <span v-if="colunaOrdenada === 'usuario'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th @click="ordenarPor('email')" class="cursor-pointer px-4 py-3 text-left">
                  Email <span v-if="colunaOrdenada === 'email'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th class="px-4 py-3 text-center">Grupos</th>
                <th class="px-4 py-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="user in usuariosOrdenados"
                :key="user.id"
                class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <td class="px-4 py-3">{{ user.first_name }} {{ user.last_name }}</td>
                <td class="px-4 py-3">{{ user.username }}</td>
                <td class="px-4 py-3">{{ user.email || '-' }}</td>
                <td class="px-4 py-3 text-center">
                  <span
                    v-for="group in user.groups"
                    :key="group"
                    class="inline-block bg-blue-100 text-blue-800 text-xs uppercase font-semibold mr-2 px-2.5 py-0.5 rounded dark:bg-blue-200 dark:text-blue-800"
                  >
                    {{ group }}
                  </span>
                </td>
                <td class="px-4 py-3 text-center">
                  <button @click="openModal(user)" class="flex items-center gap-1 text-blue-500 hover:text-blue-700">
                    <Edit2Icon class="w-4 h-4" /> Editar Grupos
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="activeTab === 'usuarios'" class="sm:hidden space-y-6">
          <div
            v-for="user in usuariosOrdenados"
            :key="user.id"
            class="bg-white dark:bg-gray-900 shadow-lg rounded-xl p-6 border border-gray-100 dark:border-gray-700 transition transform hover:scale-105"
          >
            <div class="flex justify-between items-center">
              <h3 class="text-xl font-bold text-gray-900 dark:text-white">
                {{ user.first_name }} {{ user.last_name }}
              </h3>
              <button @click="openModal(user)" class="text-blue-500 hover:text-blue-700">
                <Edit2Icon class="w-5 h-5" />
              </button>
            </div>
            <p class="text-sm text-gray-700 dark:text-gray-300"><strong>Usuário:</strong> {{ user.username }}</p>
            <p class="text-sm text-gray-700 dark:text-gray-300"><strong>Email:</strong> {{ user.email || '-' }}</p>
            <p class="text-sm text-gray-700 dark:text-gray-300">
              <strong>Grupos:</strong>
              <span v-for="group in user.groups" :key="group" class="ml-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded dark:bg-blue-200 dark:text-blue-800">
                {{ group }}
              </span>
            </p>
          </div>
        </div>

        <div v-if="activeTab === 'permissoes'" class="space-y-6">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto dark:text-white text-gray-800">
            <table class="w-full">
              <thead class="bg-blue-500 dark:bg-blue-600 text-white dark:text-gray-300 text-sm uppercase tracking-wide">
                <tr>
                  <th class="px-4 py-3 text-left">Grupo</th>
                  <th class="px-4 py-3 text-center">Permissões Ativas</th>
                  <th class="px-4 py-3 text-center">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="grupo in gruposComPermissoesFiltrados"
                  :key="grupo.grupo_id"
                  class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <td class="px-4 py-3 font-medium">{{ grupo.grupo_nome }}</td>
                  <td class="px-4 py-3 text-center">{{ contarPermissoesAtivas(grupo) }}</td>
                  <td class="px-4 py-3 text-center">
                    <button @click="openPermissaoUnificadaModal(grupo)" class="flex items-center gap-1 text-blue-500 hover:text-blue-700">
                      <Edit2Icon class="w-4 h-4" /> Editar Permissões
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-if="isModalOpen"
          class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-96 animate-fade-in">
            <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <LockIcon class="w-5 h-5 text-blue-500" />
              Editar Grupos de {{ currentUser.first_name }} {{ currentUser.last_name }}
            </h2>

            <div class="space-y-2 mb-4">
              <div
                v-for="group in availablePermissions"
                :key="group.id"
                class="flex items-center"
              >
                <input
                  :id="'group-' + group.id"
                  type="checkbox"
                  :value="group.name"
                  v-model="currentUser.permissions"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label :for="'group-' + group.id" class="ml-2 text-sm font-medium uppercase text-gray-900 dark:text-gray-300">
                  {{ group.name }}
                </label>
              </div>
            </div>

            <div class="flex justify-end space-x-3">
              <button
                @click="closeModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                <XIcon class="w-4 h-4" /> Cancelar
              </button>
              <button
                @click="savePermissions"
                class="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
              >
                <SaveIcon class="w-4 h-4" />
                <span>Salvar</span>
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="isPermissaoUnificadaModalOpen && grupoPermissaoAtual"
          class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
        >
          <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-4xl animate-fade-in">
            <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <LockIcon class="w-5 h-5 text-blue-500" />
              Permissões de {{ grupoPermissaoAtual.grupo_nome }}
            </h2>

            <div class="mb-4">
              <select
                v-model="appFilterModal"
                class="w-full sm:w-64 border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="todos">Todos os apps</option>
                <option v-for="app in appsParaFiltro" :key="app.value" :value="app.value">
                  {{ app.label }}
                </option>
              </select>
            </div>

            <div class="flex flex-col md:flex-row gap-4 mb-6 h-96">
              <div class="flex-1 flex flex-col border rounded-lg dark:border-gray-600 overflow-hidden">
                <div class="bg-gray-100 dark:bg-gray-700 p-3 font-semibold text-center border-b dark:border-gray-600 text-gray-700 dark:text-gray-200">
                  Permissões Disponíveis
                </div>
                <div class="flex-1 overflow-y-auto p-2 space-y-1 bg-white dark:bg-gray-800">
                  <div
                    v-for="campo in permissoesDisponiveisModal"
                    :key="campo.app + '-' + campo.key"
                    @click="togglePermissaoModal(campo, true)"
                    class="p-2 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 flex justify-between items-center group transition-colors border border-transparent hover:border-blue-200 dark:hover:border-blue-800"
                  >
                    <span class="text-sm text-gray-700 dark:text-gray-300">
                      {{ campo.label }} <span class="text-xs text-gray-400">({{ campo.appLabel }})</span>
                    </span>
                    <ArrowRight class="w-4 h-4 text-gray-400 group-hover:text-blue-500" />
                  </div>
                  <div v-if="permissoesDisponiveisModal.length === 0" class="text-center text-gray-400 text-sm p-4">
                    Nenhuma permissão disponível
                  </div>
                </div>
              </div>

              <div class="flex md:flex-col justify-center gap-2 items-center">
                <button
                  @click="setTodasPermissoesVisiveis(true)"
                  class="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900 text-gray-600 dark:text-gray-300 transition-colors"
                  title="Habilitar Todas"
                >
                  <ChevronsRight class="w-5 h-5" />
                </button>
                <button
                  @click="setTodasPermissoesVisiveis(false)"
                  class="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-900 text-gray-600 dark:text-gray-300 transition-colors"
                  title="Desabilitar Todas"
                >
                  <ChevronsLeft class="w-5 h-5" />
                </button>
              </div>

              <div class="flex-1 flex flex-col border rounded-lg dark:border-gray-600 overflow-hidden">
                <div class="bg-blue-50 dark:bg-blue-900/20 p-3 font-semibold text-center border-b dark:border-gray-600 text-blue-700 dark:text-blue-300">
                  Permissões Habilitadas
                </div>
                <div class="flex-1 overflow-y-auto p-2 space-y-1 bg-white dark:bg-gray-800">
                  <div
                    v-for="campo in permissoesHabilitadasModal"
                    :key="campo.app + '-' + campo.key"
                    @click="togglePermissaoModal(campo, false)"
                    class="p-2 rounded cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/30 flex justify-between items-center group transition-colors border border-transparent hover:border-red-200 dark:hover:border-red-800"
                  >
                    <ArrowLeft class="w-4 h-4 text-gray-400 group-hover:text-red-500" />
                    <span class="text-sm text-gray-900 dark:text-white font-medium">
                      {{ campo.label }} <span class="text-xs text-gray-400">({{ campo.appLabel }})</span>
                    </span>
                  </div>
                  <div v-if="permissoesHabilitadasModal.length === 0" class="text-center text-gray-400 text-sm p-4">
                    Nenhuma permissão habilitada
                  </div>
                </div>
              </div>
            </div>

            <div class="flex justify-end space-x-3">
              <button
                @click="closePermissaoUnificadaModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg"
              >
                <XIcon class="w-4 h-4" /> Cancelar
              </button>
              <button
                @click="savePermissoesGrupoUnificadas"
                class="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg"
              >
                <SaveIcon class="w-4 h-4" />
                <span>Salvar</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseTemplate>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { LockIcon, Edit2Icon, XIcon, SaveIcon, SquareArrowLeft, ArrowRight, ArrowLeft, ChevronsRight, ChevronsLeft } from 'lucide-vue-next'
import BaseTemplate from '../templates/BaseTemplate.vue'
import apiClient from '@/services/authService'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()
const voltar = () => router.go(-1)

const users = ref([])
const filter = ref('')
const groupFilter = ref('')
const error = ref(null)
const isModalOpen = ref(false)
const currentUser = ref({})
const activeTab = ref('usuarios')

const colunaOrdenada = ref('nome')
const ordemAscendente = ref(true)

const availablePermissions = ref([])
const endpointPermissionsCatalog = ref({})
const appsCatalog = ref([])

const isPermissaoUnificadaModalOpen = ref(false)
const grupoPermissaoAtual = ref(null)
const appFilterModal = ref('todos')

const appLabelMap = {
  auth: 'Autenticação',
  profile: 'Perfil',
  daysoff: 'Folgas',
  scan: 'Scan',
  events: 'Eventos',
  entity: 'Entidade',
  enterprises: 'Empresas',
  equipments: 'Equipamentos',
  pending: 'Pendências',
  supplier: 'Fornecedores',
  guardian: 'Guardian',
  inventoryit: 'Inventário TI',
  meet: 'Meet',
  news: 'Notícias',
  feed: 'Feed',
  files: 'Documentos',
  audit: 'Auditoria',
  responsible: 'Responsáveis',
  notifications: 'Notificações',
  tickets: 'Chamados',
  'eventos-adversos': 'Eventos Adversos',
  indicadores: 'Indicadores',
  juridico: 'Jurídico',
  preparo: 'Preparo',
  'portal-acessos': 'Portal de Acessos',
  changelog: 'Changelog'
}

function appSlugToLabel(slug) {
  if (appLabelMap[slug]) return appLabelMap[slug]
  return (slug || '')
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function endpointKeyToLabel(key) {
  if (!key || typeof key !== 'string') return ''
  const parts = key.split(' ')
  if (parts.length < 2) return key

  const method = parts[0].toUpperCase()
  const path = parts.slice(1).join(' ').trim()
  const pathParts = path.split('/').filter(Boolean)

  const semPrefixo = pathParts.slice(0, 2).join('/') === 'api/v1'
    ? pathParts.slice(2)
    : pathParts

  const appSlug = semPrefixo[0] || ''
  const recursos = semPrefixo.slice(1)
  const temId = recursos.some(item => /^\{.+\}$/.test(item))
  const recursosSemId = recursos.filter(item => !/^\{.+\}$/.test(item))

  const segmentoLabelMap = {
    comments: 'comentarios',
    answers: 'respostas',
    posts: 'publicacoes',
    gruposfeed: 'grupos do feed',
    gruposfeedusuarios: 'usuarios dos grupos do feed',
    reacoes: 'reacoes',
    historico: 'historico',
    gerenciar: 'gestao',
    relatorio: 'relatorios',
    dados: 'dados',
    anexos: 'anexos',
    anexo: 'anexo',
    locais: 'locais',
    estatisticas: 'estatisticas',
    graficos: 'graficos',
    groups: 'grupos',
    users: 'usuarios',
    permissions: 'permissoes',
    catalog: 'catalogo',
    members: 'membros',
    upload: 'upload',
    download: 'download'
  }

  const nomeRecurso = recursosSemId
    .map(segmento => {
      const valor = segmentoLabelMap[segmento] || segmento
      return valor.replace(/[-_]/g, ' ').toLowerCase()
    })
    .join(' - ')

  const acaoPorMetodo = {
    GET: temId ? 'Visualizar' : 'Listar',
    POST: 'Criar',
    PUT: 'Editar',
    PATCH: 'Atualizar',
    DELETE: 'Excluir'
  }

  const acao = acaoPorMetodo[method] || 'Acessar'
  const alvo = nomeRecurso || appSlugToLabel(appSlug).toLowerCase()
  return `${acao} ${alvo}`.trim()
}

const appsParaFiltro = computed(() =>
  appsCatalog.value.map(app => ({
    value: app,
    label: appSlugToLabel(app)
  }))
)

const filteredUsers = computed(() =>
  users.value.filter(user =>
    ((user.first_name || '') + ' ' + (user.last_name || ''))
      .toLowerCase()
      .includes(filter.value.toLowerCase()) ||
    (user.username || '').toLowerCase().includes(filter.value.toLowerCase()) ||
    (user.email || '').toLowerCase().includes(filter.value.toLowerCase())
  )
)

const usuariosOrdenados = computed(() => {
  const usuariosFiltrados = filteredUsers.value.slice()
  return usuariosFiltrados.sort((a, b) => {
    let valorA
    let valorB
    switch (colunaOrdenada.value) {
      case 'nome':
        valorA = `${a.first_name} ${a.last_name}`.toLowerCase()
        valorB = `${b.first_name} ${b.last_name}`.toLowerCase()
        break
      case 'usuario':
        valorA = a.username.toLowerCase()
        valorB = b.username.toLowerCase()
        break
      case 'email':
        valorA = a.email.toLowerCase()
        valorB = b.email.toLowerCase()
        break
      default:
        return 0
    }
    if (valorA < valorB) return ordemAscendente.value ? -1 : 1
    if (valorA > valorB) return ordemAscendente.value ? 1 : -1
    return 0
  })
})

const gruposComPermissoes = computed(() => {
  return availablePermissions.value.map(group => {
    const appPermissions = {}
    const endpointPermissions = {}
    appsCatalog.value.forEach(app => {
      appPermissions[app] = !!group?.app_permissions?.[app]
      const catalogoApp = endpointPermissionsCatalog.value?.[app] || {}
      endpointPermissions[app] = {}
      Object.keys(catalogoApp).forEach(endpointKey => {
        endpointPermissions[app][endpointKey] =
          group?.endpoint_permissions?.[app]?.[endpointKey] ?? !!catalogoApp[endpointKey]
      })
    })

    return {
      grupo_id: group.id,
      grupo_nome: group.name,
      app_permissions: appPermissions,
      endpoint_permissions: endpointPermissions
    }
  })
})

const gruposComPermissoesFiltrados = computed(() => {
  const filtro = groupFilter.value.trim().toLowerCase()
  if (!filtro) return gruposComPermissoes.value
  return gruposComPermissoes.value.filter(grupo =>
    (grupo.grupo_nome || '').toLowerCase().includes(filtro)
  )
})

function listarItensPermissaoGrupo(grupo, filtroApp) {
  if (!grupo) return []
  const apps = filtroApp === 'todos' ? appsCatalog.value : [filtroApp]
  const itens = []

  apps.forEach(app => {
    if (!appsCatalog.value.includes(app)) return
    const appLabel = appSlugToLabel(app)

    itens.push({
      id: `${app}::__app_access__`,
      app,
      appLabel,
      key: '__app_access__',
      label: 'Acesso ao app',
      ativo: !!grupo.app_permissions?.[app],
      isAppAccess: true
    })

    const catalogoApp = endpointPermissionsCatalog.value?.[app] || {}
    Object.keys(catalogoApp).forEach(endpointKey => {
      itens.push({
        id: `${app}::${endpointKey}`,
        app,
        appLabel,
        key: endpointKey,
        label: endpointKeyToLabel(endpointKey),
        ativo: !!grupo.endpoint_permissions?.[app]?.[endpointKey],
        isAppAccess: false
      })
    })
  })

  return itens
}

const permissoesVisiveisModal = computed(() =>
  listarItensPermissaoGrupo(grupoPermissaoAtual.value, appFilterModal.value)
)

const permissoesDisponiveisModal = computed(() =>
  permissoesVisiveisModal.value.filter(item => !item.ativo)
)

const permissoesHabilitadasModal = computed(() =>
  permissoesVisiveisModal.value.filter(item => item.ativo)
)

function ordenarPor(coluna) {
  if (colunaOrdenada.value === coluna) {
    ordemAscendente.value = !ordemAscendente.value
  } else {
    colunaOrdenada.value = coluna
    ordemAscendente.value = true
  }
}

function contarPermissoesAtivas(grupo) {
  return listarItensPermissaoGrupo(grupo, 'todos').reduce(
    (acc, item) => acc + (item.ativo ? 1 : 0),
    0
  )
}

function openPermissaoUnificadaModal(grupo) {
  grupoPermissaoAtual.value = JSON.parse(JSON.stringify(grupo))
  appFilterModal.value = 'todos'
  isPermissaoUnificadaModalOpen.value = true
}

function closePermissaoUnificadaModal() {
  isPermissaoUnificadaModalOpen.value = false
  grupoPermissaoAtual.value = null
}

function togglePermissaoModal(campo, ativo) {
  if (!grupoPermissaoAtual.value) return
  if (campo.isAppAccess) {
    grupoPermissaoAtual.value.app_permissions[campo.app] = ativo
    if (!ativo && grupoPermissaoAtual.value.endpoint_permissions[campo.app]) {
      Object.keys(grupoPermissaoAtual.value.endpoint_permissions[campo.app]).forEach(key => {
        grupoPermissaoAtual.value.endpoint_permissions[campo.app][key] = false
      })
    }
    return
  }
  if (!grupoPermissaoAtual.value.endpoint_permissions[campo.app]) {
    grupoPermissaoAtual.value.endpoint_permissions[campo.app] = {}
  }
  grupoPermissaoAtual.value.endpoint_permissions[campo.app][campo.key] = ativo
}

function setTodasPermissoesVisiveis(ativo) {
  permissoesVisiveisModal.value.forEach(campo => {
    togglePermissaoModal(campo, ativo)
  })
}

onMounted(() => {
  if (
    route.query.tab === 'permissoes' ||
    route.hash === '#permissoes'
  ) {
    activeTab.value = 'permissoes'
  }
  fetchUsers()
  fetchCatalogoPermissoes().then(() => {
    fetchGroups()
  })
})

async function fetchUsers() {
  try {
    const response = await apiClient.get('/api/v1/auth/users/')
    const data = response.data
    users.value = data.map(user => ({
      ...user,
      groups: user.groups || []
    }))
  } catch (err) {
    error.value = 'Erro ao carregar usuários'
  }
}

async function fetchGroups() {
  try {
    const response = await apiClient.get('/api/v1/auth/groups/')
    const data = response.data
    availablePermissions.value = Array.isArray(data) ? data : []
  } catch (err) {
    error.value = 'Erro ao carregar grupos'
  }
}

async function fetchCatalogoPermissoes() {
  try {
    const response = await apiClient.get('/api/v1/auth/groups/permissions/catalog/')
    const data = response.data || {}
    appsCatalog.value = Array.isArray(data.apps) ? data.apps : []
    endpointPermissionsCatalog.value = data.endpoint_permissions_catalog || {}
  } catch (err) {
    error.value = 'Erro ao carregar catálogo de permissões'
    appsCatalog.value = []
    endpointPermissionsCatalog.value = {}
  }
}

function openModal(user) {
  currentUser.value = { ...user, permissions: [...user.groups] }
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
}

async function savePermissions() {
  try {
    const payload = {
      groups: currentUser.value.permissions
        .map(g => {
          const group = availablePermissions.value.find(p => p.name === g)
          return group ? group.id : null
        })
        .filter(g => g !== null)
    }

    await apiClient.put(`/api/v1/auth/users/${currentUser.value.id}/groups/`, payload)
    await fetchUsers()
    await fetchGroups()
    closeModal()
  } catch (err) {
    error.value = 'Erro ao salvar grupos do usuário'
  }
}

async function savePermissoesGrupoUnificadas() {
  if (!grupoPermissaoAtual.value) return
  try {
    const payload = {
      app_permissions: {},
      endpoint_permissions: {}
    }

    appsCatalog.value.forEach(app => {
      payload.app_permissions[app] = !!grupoPermissaoAtual.value.app_permissions?.[app]
      const catalogoApp = endpointPermissionsCatalog.value?.[app] || {}
      payload.endpoint_permissions[app] = {}
      Object.keys(catalogoApp).forEach(endpointKey => {
        payload.endpoint_permissions[app][endpointKey] =
          !!grupoPermissaoAtual.value.endpoint_permissions?.[app]?.[endpointKey]
      })
    })

    await apiClient.put(
      `/api/v1/auth/groups/${grupoPermissaoAtual.value.grupo_id}/`,
      payload
    )

    await fetchGroups()
    closePermissaoUnificadaModal()
  } catch (err) {
    error.value = 'Erro ao salvar permissões do grupo'
  }
}
</script>
